import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ScrollText, RefreshCcw, Search, ExternalLink } from "lucide-react";
import { formatNotes, hasVisibleNotes, formatDiffValue } from "@/lib/safeDisplay";

// ---------------------------------------------------------------------------
// MP12 Advanced Features — Module 1: Proof Ledger.
//
// Curated read-only view over audit_logs restricted to significant
// operational events. This is not a second write-path: every row was
// already an audit_logs entry — we just filter, group, and annotate.
// ---------------------------------------------------------------------------

const ACTION_LABELS = {
  "communication.created":                "Comm · Created",
  "communication.updated":                "Comm · Updated",
  "communication.classification_changed": "Comm · Classification changed",
  "communication.message_added":          "Comm · Message added",
  "communication.message_sent":           "Comm · Message sent",
  "communication.message_outbox_held":    "Comm · Held in Outbox",
  "communication.secure_link_created":    "Comm · Secure link created",
  "communication.secure_link_accessed":   "Comm · Secure link accessed",
  "communication.file_shared":            "Comm · File shared",
  "track.created":                        "Track · Created",
  "track.updated":                        "Track · Updated",
  "track.deleted":                        "Track · Deleted",
  "track.clearance.decision":             "Track · Clearance decision",
  "track.attorney_review.flagged":        "Track · Attorney review flagged",
  "track.attorney_review.cleared":        "Track · Attorney review cleared",
  "track.legacy_rights.cleared":          "Track · Legacy PRO/IPI cleared",
  "track.beta_flags_changed":             "Track · Beta flags changed",
  "demo.created":                         "Demo · Created",
  "demo.deleted":                         "Demo · Deleted",
  "demo.file.deleted":                    "Demo · Attachment removed",
  "submission.deleted":                   "Submission · Deleted",
  "submission.sent":                      "Submission · Sent",
  "license_request.deleted":              "License Request · Deleted",
  "consultation.deleted":                 "Consultation · Deleted",
  "human.override.used":                  "Human override used",
  "compliance.decision.changed":          "Compliance decision changed",
  "track.rights_parties_updated":         "Track · Rights parties updated",
};

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return dt; }
}

function actionColor(a) {
  if (!a) return "bg-secondary text-foreground/80";
  if (a.endsWith(".deleted")) return "bg-red-100 text-red-900 border border-red-500/40";
  if (a.endsWith(".outbox_held")) return "bg-amber-100 text-amber-900 border border-amber-500/40";
  if (a.endsWith(".sent") || a.endsWith(".created")) return "bg-emerald-100 text-emerald-900 border border-emerald-500/40";
  if (a.includes("legacy") || a.includes("classification")) return "bg-blue-100 text-blue-900 border border-blue-500/40";
  return "bg-secondary text-foreground/80";
}

function BeforeAfter({ before, after }) {
  if (!before && !after) return null;
  const keys = Array.from(new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after || {}),
  ]));
  if (keys.length === 0) return null;
  // Stable serialisation used only for equality comparison; never rendered.
  const stableKey = (v) => {
    if (v == null) return "";
    if (typeof v === "object") {
      try { return JSON.stringify(v); } catch (_e) { return String(v); }
    }
    return String(v);
  };
  return (
    <div className="mt-1 grid gap-x-3 gap-y-0.5 text-[11px] font-mono text-muted-foreground">
      {keys.map((k) => {
        const b = (before || {})[k];
        const a = (after || {})[k];
        if (stableKey(b) === stableKey(a)) return null;
        const bStr = formatDiffValue(b);
        const aStr = formatDiffValue(a);
        return (
          <div key={k} className="flex flex-wrap gap-1">
            <span className="text-muted-foreground/70">{k}:</span>
            <span className="text-red-800/80 line-through max-w-[400px] truncate" title={bStr}>{bStr}</span>
            <span className="opacity-50">→</span>
            <span className="text-emerald-800/80 max-w-[400px] truncate" title={aStr}>{aStr}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ProofLedger() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [fAction, setFAction] = useState("all");
  const [fRecordType, setFRecordType] = useState("");
  const [fRecordId, setFRecordId] = useState("");
  const [fLimit, setFLimit] = useState(200);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: fLimit };
      if (fAction !== "all") params.action_type = fAction;
      if (fRecordType.trim()) params.record_type = fRecordType.trim();
      if (fRecordId.trim()) params.record_id = fRecordId.trim();
      const r = await api.get("/proof-ledger", { params });
      setRows(r.data?.rows || []);
      setCount(r.data?.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Proof Ledger");
    } finally {
      setLoading(false);
    }
  }, [fAction, fRecordType, fRecordId, fLimit]);

  useEffect(() => { load(); }, [load]);

  const actionOptions = Object.keys(ACTION_LABELS);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="proof-ledger">
      <div className="flex flex-wrap items-center gap-2">
        <ScrollText className="w-5 h-5" style={{ color: "hsl(347 60% 22%)" }} />
        <h1 className="font-display text-[22px]">Proof Ledger</h1>
        <span className="text-[11px] text-muted-foreground">
          Advanced Features · Curated view of significant operational events. Sourced from audit_logs.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="proof-refresh">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="grid md:grid-cols-[260px_180px_260px_120px] gap-2 items-end">
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Event</Label>
              <Select value={fAction} onValueChange={setFAction}>
                <SelectTrigger className="h-8" data-testid="proof-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value="all">All ledger events</SelectItem>
                  {actionOptions.map((a) => (
                    <SelectItem key={a} value={a}>{ACTION_LABELS[a]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Record type</Label>
              <Input value={fRecordType} onChange={(e) => setFRecordType(e.target.value)} placeholder="e.g. track / demo" className="h-8 font-mono" data-testid="proof-filter-type" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Record id</Label>
              <Input value={fRecordId} onChange={(e) => setFRecordId(e.target.value)} placeholder="Exact UUID or short id" className="h-8 font-mono" data-testid="proof-filter-id" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wide">Limit</Label>
              <Select value={String(fLimit)} onValueChange={(v) => setFLimit(parseInt(v, 10))}>
                <SelectTrigger className="h-8" data-testid="proof-filter-limit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="200">200</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="p-3 pb-2">
          <CardTitle className="text-[13px] flex items-center gap-2">
            <Search className="w-3.5 h-3.5 opacity-70" />
            Ledger events
            <span className="ml-auto text-[11px] text-muted-foreground" data-testid="proof-count">{count}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <div className="p-4 text-[12px] text-muted-foreground">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-4 text-[12px] text-muted-foreground" data-testid="proof-empty">
              No matching events.
            </div>
          )}
          <div className="divide-y">
            {rows.map((r, idx) => (
              <div key={r.id || r.audit_id || `${r.record_id || "row"}-${r.created_at || idx}`} className="px-3 py-2" data-testid={`proof-row-${r.id || idx}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[11px] text-muted-foreground w-[160px] shrink-0">{fmt(r.created_at)}</span>
                  <span className={`text-[10.5px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${actionColor(r.action_type)}`}>
                    {ACTION_LABELS[r.action_type] || r.action_type}
                  </span>
                  {r.material && (
                    <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[hsl(347_72%_22%)]/10 text-[hsl(347_72%_22%)] border border-[hsl(347_72%_22%)]/40" data-testid="proof-material-badge">
                      Material
                    </span>
                  )}
                  {r.severity && r.severity !== "info" && (
                    <span className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${
                      r.severity === "critical" ? "bg-red-100 text-red-900 border border-red-300" :
                      r.severity === "high" ? "bg-amber-100 text-amber-900 border border-amber-300" :
                      "bg-slate-100 text-slate-800 border border-slate-300"
                    }`}>
                      {r.severity}
                    </span>
                  )}
                  <span className="text-[12px] text-muted-foreground">
                    <span className="font-mono">{r.record_type}</span>
                    {r.record_id && (
                      <>
                        {" · "}
                        <span className="font-mono">{(r.record_id || "").slice(0, 12)}</span>
                      </>
                    )}
                  </span>
                  <span className="ml-auto text-[11.5px] text-muted-foreground">
                    by <span className="font-mono">{r.actor_email || "system"}</span>
                  </span>
                </div>
                {r.summary && (
                  <div className="text-[12px] text-foreground/85 mt-1 font-medium" data-testid="proof-summary">{r.summary}</div>
                )}
                {hasVisibleNotes(r.notes) && (
                  <div className="text-[11.5px] text-muted-foreground mt-0.5">{formatNotes(r.notes, { fallback: "\u2014" })}</div>
                )}
                <BeforeAfter before={r.before_value} after={r.after_value} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
